import { useEffect, useRef, useState } from "react";
import {
  authHeaders,
  handleUnauthorized,
} from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Lightweight, isolated convert-to-lead modal.
 * Required: email. Optional: contact_name, phone, notes.
 * Calls POST /api/mockups/{id}/convert and returns the conversion to the parent.
 */
export default function ConvertMockupModal({ mockup, onClose, onConverted }) {
  const [email, setEmail] = useState("");
  const [contactName, setContactName] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const emailInputRef = useRef(null);

  useEffect(() => {
    emailInputRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && !submitting) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, submitting]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const trimmed = email.trim();
    if (!trimmed || !EMAIL_RE.test(trimmed)) {
      setError("A valid contact email is required.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/mockups/${mockup.id}/convert`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({
          email: trimmed,
          contact_name: contactName.trim(),
          phone: phone.trim(),
          notes: notes.trim(),
        }),
      });
      if (res.status === 401 || res.status === 403) {
  handleUnauthorized();
  return;
}
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        setError(j.detail?.[0]?.msg || j.detail || `HTTP ${res.status}`);
        return;
      }
      const data = await res.json();
      onConverted(data);
    } catch (err) {
      console.error("Convert failed:", err);
      setError("Network error. Please retry.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4"
      role="dialog"
      aria-modal="true"
      data-testid="convert-mockup-modal"
      onClick={(e) => {
        if (e.target === e.currentTarget && !submitting) onClose();
      }}
    >
      <form
        onSubmit={submit}
        noValidate
        className="w-full max-w-md bg-[#121214] border border-white/10 rounded-2xl p-6 shadow-2xl"
      >
        <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#00E5FF]">
          Convert mockup
        </div>
        <h2 className="font-display font-semibold text-xl mt-1">
          {mockup.restaurant_name}
        </h2>
        <p className="text-xs text-zinc-500 mt-1">
          Creates a real lead record · payment status starts as <span className="text-zinc-300">unpaid</span>.
        </p>

        <label className="block mt-5 text-xs text-zinc-400">
          Contact email <span className="text-[#00E5FF]">*</span>
        </label>
        <input
          ref={emailInputRef}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="owner@restaurant.com"
          className="mt-1 w-full bg-[#1A1A1F] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-[#00E5FF] focus:ring-1 focus:ring-[#00E5FF]"
          data-testid="convert-email-input"
        />

        <label className="block mt-3 text-xs text-zinc-400">Contact name</label>
        <input
          type="text"
          value={contactName}
          onChange={(e) => setContactName(e.target.value)}
          placeholder="Optional"
          className="mt-1 w-full bg-[#1A1A1F] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-[#00E5FF] focus:ring-1 focus:ring-[#00E5FF]"
          data-testid="convert-name-input"
        />

        <label className="block mt-3 text-xs text-zinc-400">Phone</label>
        <input
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="Optional"
          className="mt-1 w-full bg-[#1A1A1F] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-[#00E5FF] focus:ring-1 focus:ring-[#00E5FF]"
          data-testid="convert-phone-input"
        />

        <label className="block mt-3 text-xs text-zinc-400">Notes</label>
        <textarea
          rows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Optional"
          className="mt-1 w-full bg-[#1A1A1F] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-[#00E5FF] focus:ring-1 focus:ring-[#00E5FF] resize-none"
          data-testid="convert-notes-input"
        />

        {error && (
          <div
            className="mt-4 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-300"
            data-testid="convert-error"
          >
            {error}
          </div>
        )}

        <div className="mt-6 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="text-xs text-zinc-400 hover:text-white px-4 py-2 disabled:opacity-50"
            data-testid="convert-cancel"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="bg-[#00E5FF] text-black font-semibold text-sm rounded-full px-5 py-2.5 hover:bg-[#66FFFF] transition-colors disabled:opacity-60"
            data-testid="convert-submit"
          >
            {submitting ? "Converting…" : "Convert to lead"}
          </button>
        </div>
      </form>
    </div>
  );
}
